import React, { useState } from "react";
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import Loader from '../../../components/utils/Loader';
import useFetch from '../../../hooks/useFetch';
import jsPDF from "jspdf";
import DeepSeek from '../DeepSeekFeedback';

const rubrics = {
  Mathematics: [
    { name: "Accuracy", weight: 30, score: 0, feedback: "" },
    { name: "Problem-Solving Approach", weight: 30, score: 0, feedback: "" },
    { name: "Presentation", weight: 20, score: 0, feedback: "" },
    { name: "Clarity of Steps", weight: 20, score: 0, feedback: "" },
  ],
  Science: [
    { name: "Hypothesis", weight: 25, score: 0, feedback: "" },
    { name: "Methodology", weight: 25, score: 0, feedback: "" },
    { name: "Analysis", weight: 25, score: 0, feedback: "" },
    { name: "Conclusion", weight: 25, score: 0, feedback: "" },
  ],
  English: [
    { name: "Grammar", weight: 25, score: 0, feedback: "" },
    { name: "Structure", weight: 25, score: 0, feedback: "" },
    { name: "Creativity", weight: 25, score: 0, feedback: "" },
    { name: "Argument Clarity", weight: 25, score: 0, feedback: "" },
  ],
  History: [
    { name: "Research", weight: 30, score: 0, feedback: "" },
    { name: "Argument Development", weight: 30, score: 0, feedback: "" },
    { name: "Use of Sources", weight: 20, score: 0, feedback: "" },
    { name: "Historical Accuracy", weight: 20, score: 0, feedback: "" },
  ],
  Art: [
    { name: "Creativity", weight: 30, score: 0, feedback: "" },
    { name: "Technique", weight: 30, score: 0, feedback: "" },
    { name: "Expression", weight: 20, score: 0, feedback: "" },
    { name: "Originality", weight: 20, score: 0, feedback: "" },
  ],
  Music: [
    { name: "Creativity", weight: 30, score: 0, feedback: "" },
    { name: "Technique", weight: 30, score: 0, feedback: "" },
    { name: "Expression", weight: 20, score: 0, feedback: "" },
    { name: "Originality", weight: 20, score: 0, feedback: "" },
  ],
  PhysicalEducation: [
    { name: "Skill Execution", weight: 30, score: 0, feedback: "" },
    { name: "Teamwork", weight: 30, score: 0, feedback: "" },
    { name: "Sportsmanship", weight: 20, score: 0, feedback: "" },
    { name: "Endurance & Fitness", weight: 20, score: 0, feedback: "" },
  ],
  ComputerScience: [
    { name: "Code Efficiency", weight: 30, score: 0, feedback: "" },
    { name: "Problem-Solving", weight: 30, score: 0, feedback: "" },
    { name: "Debugging & Error Handling", weight: 20, score: 0, feedback: "" },
    { name: "Documentation & Comments", weight: 20, score: 0, feedback: "" },
  ],
  BusinessStudies: [
    { name: "Market Analysis", weight: 30, score: 0, feedback: "" },
    { name: "Critical Thinking", weight: 30, score: 0, feedback: "" },
    { name: "Data Interpretation", weight: 20, score: 0, feedback: "" },
    { name: "Case Study Application", weight: 20, score: 0, feedback: "" },
  ],
  Psychology: [
    { name: "Research Methodology", weight: 30, score: 0, feedback: "" },
    { name: "Analysis & Interpretation", weight: 30, score: 0, feedback: "" },
    { name: "Case Study Application", weight: 20, score: 0, feedback: "" },
    { name: "Argument Clarity", weight: 20, score: 0, feedback: "" },
  ],
  ForeignLanguages: [
    { name: "Grammar & Sentence Structure", weight: 30, score: 0, feedback: "" },
    { name: "Vocabulary Usage", weight: 25, score: 0, feedback: "" },
    { name: "Pronunciation & Fluency", weight: 25, score: 0, feedback: "" },
    { name: "Cultural Context", weight: 20, score: 0, feedback: "" },
  ],
  EnvironmentalScience: [
    { name: "Understanding of Concepts", weight: 30, score: 0, feedback: "" },
    { name: "Data & Graph Analysis", weight: 30, score: 0, feedback: "" },
    { name: "Sustainability Insights", weight: 20, score: 0, feedback: "" },
    { name: "Field Work / Practical Application", weight: 20, score: 0, feedback: "" },
  ],
  Philosophy: [
    { name: "Logical Reasoning", weight: 30, score: 0, feedback: "" },
    { name: "Ethical Argumentation", weight: 30, score: 0, feedback: "" },
    { name: "Application of Theories", weight: 20, score: 0, feedback: "" },
    { name: "Clarity & Organization", weight: 20, score: 0, feedback: "" },
  ],
};

const AI_Feedback = (score) => {
  if (score >= 9) return "Excellent! Keep up the great work!";
  if (score >= 7) return "Good job! A little improvement needed.";
  if (score >= 5) return "Average performance. Focus on key areas.";
  return "Needs improvement. Consider reviewing concepts.";
};

const RubricCalculator = () => {
  const authState = useSelector(state => state.authReducer);
  const navigate = useNavigate();
  const [userId, setUserId] = useState(authState.user._id);
  const [userName, setUserName] = useState(authState.user.name);
  const [userCode, setUserCode] = useState(authState.user.think5whyscode);
  const [loading, setLoading] = useState(false);
  const [subject, setSubject] = useState("Mathematics");
  const [selectedSubject, setSelectedSubject] = useState("Mathematics");
  const [studentInfo, setStudentInfo] = useState({
    studentCode: "",
    session: "",
    grade: "",
    section: "",
    teacherCode: "",
    institutionCode: "",
    question: "",
    studentAnswer: "",
    aiPrompt: "",
    aiFeedback: ""
  });

  const [criteria, setCriteria] = useState(rubrics[subject]);
  const [savedRubrics, setSavedRubrics] = useState([]);
  
  const handleSubjectChange = (event) => {
    const newSubject = event.target.value;
    setSelectedSubject(newSubject);
    setCriteria(rubrics[newSubject]); 
  };

  const handleScoreChange = (index, value) => {
    const newCriteria = [...criteria];
    newCriteria[index].score = value;
    setCriteria(newCriteria);
  };

  const handleStudentInfoChange = (e) => {
    setStudentInfo({ ...studentInfo, [e.target.name]: e.target.value });
  };

  // const handleQuestionChange = (e) => {
  //   setStudentInfo({ ...studentInfo, [e.target.name]: e.target.value });
  // };

  // const handleStudentAnswerChange = (e) => {
  //   setStudentInfo({ ...studentInfo, [e.target.name]: e.target.value });
  // };

  const handleQuestionChange = (e) => {
    setStudentInfo((prev) => ({ ...prev, question: e.target.value }));
  };
  
  const handleStudentAnswerChange = (e) => {
    setStudentInfo((prev) => ({ ...prev, studentAnswer: e.target.value }));
  };

  const [aiClicked, setAIClicked] = useState(false);
  const generateAIPrompt = () => {
    if (!rubrics || !rubrics[selectedSubject]) {
      console.error("Rubrics or selected subject is undefined");
      return;
    }
    setAIClicked(!aiClicked);
    const criteriaList = rubrics[selectedSubject]
      .map(({ criterion, description }) => `${criterion}: ${description}`)
      .join("; ");
  
    const prompt = `Evaluate the student's answer based on the following criteria for ${selectedSubject}: ${criteriaList}.\nStudent's answer: ${studentInfo.studentAnswer}`;
  
    setStudentInfo((prev) => ({ ...prev, aiPrompt: prompt }));
  };
  
  const calculateTotal = () => {
    return criteria.reduce((acc, criterion) => acc + (criterion.score * criterion.weight) / 100, 0);
  };
  

  const getGrade = (score) => {
    if (score >= 90) return "A+";
    if (score >= 80) return "A";
    if (score >= 70) return "B";
    if (score >= 60) return "C";
    return "D";
  };

  const saveRubric = async () => {
    if (!studentInfo.question.trim()) return;
    // console.log("save rubric studentInfo: ",studentInfo);
    setLoading(true);
    try {
      const response = await axios.post("/api/rubric/save", { 
        teacherCode: userCode,
        syudentCode: studentInfo.studentCode,
        academicSession: studentInfo.academicSession,
        className: studentInfo.className,
        sectionName: studentInfo.sectionName,
        subject: studentInfo.subject,
        question: studentInfo.question,
        answer: studentInfo.answer,
        aiPrompt: studentInfo.aiPrompt,
        aiFeedback: studentInfo.aiFeedback,
        score: studentInfo.score,
        grade: studentInfo.grade,
        analyzedAt: studentInfo.analyzedAt,
        institutionCode: studentInfo.institutionCode,});
      setResults(response.data.result);
    } catch (error) {
      console.error("Error saving Rubric record!", error);
    }
    setLoading(false);
  };

  const DEEPSEEK_API_KEY = 'sk-524f8673a33743c8a3b1e3f53ff00659';
  const [myPara, setMyPara] = useState(""); 
  const [feedback, setFeedback] = useState(""); 
  const [isLoading, setIsLoading] = useState(false); 
  const getFeedbackFromDeepSeek = async (q,a) => {
    setIsLoading(true); 
    // console.log("studentInfo.question: ",studentInfo.question);
    // console.log("studentInfo.studentAnswer: ",studentInfo.studentAnswer);

    try {
      const prompt = `Evaluate the following question and answer based on the following criteria: 
      1. Clarity
      2. Grammar
      3. Coherence
      4. Overall Quality
      
      Question: ${studentInfo.question};
     
      Answer: ${studentInfo.studentAnswer}`;

      const payload = {
        prompt: prompt,
        max_tokens: 1000, 
        temperature: 0.7, // Adjust for creativity vs. precision
        model:"deepseek-reasoner",
      };
// console.log("payload: ",payload);
// console.log("studentInfo.question: ",studentInfo.question);
// console.log("studentInfo.studentAnswer: ",studentInfo.studentAnswer);
      const response = await fetch('https://api.deepseek.com/v1/complete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${DEEPSEEK_API_KEY}`,
        },
        mode: 'no-cors',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }

      const data = await response.json();
      setFeedback(data.choices[0].text); // Assuming the response contains 'choices'
    } catch (error) {
      console.error('Error fetching feedback from DeepSeek:', error);
      setFeedback('Failed to fetch feedback. Please try again.');
    } finally {
      setIsLoading(false); // Stop loading
    }
  };

  const generatePDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width - 40;
    let y = 10;
    const lineHeight = 10;

    const addWrappedText = (text, x, y) => {
      const splitText = doc.splitTextToSize(text, pageWidth);
      doc.text(splitText, x, y);
      return y + splitText.length * lineHeight;
    };

    y = addWrappedText("Rubric Report", 20, y);
    y += 5;

    Object.entries(studentInfo).forEach(([key, value]) => {
      y = addWrappedText(`${key}: ${value}`, 20, y);
      y += 5;
    });

    y += 10;
    y = addWrappedText(`Subject: ${subject}`, 20, y);
    y += 5;

    criteria.forEach(({ name, weight, score, feedback }) => {
      y = addWrappedText(`Criteria: ${name}`, 20, y);
      y = addWrappedText(`Weight: ${weight}%`, 20, y);
      y = addWrappedText(`Score: ${score}`, 20, y);
      y = addWrappedText(`Feedback: ${feedback}`, 20, y);
      y += 10;
    });

    const totalScore = calculateTotal();
    const grade = getGrade(totalScore);
    y = addWrappedText(`Total Score: ${totalScore}`, 20, y);
    y = addWrappedText(`Final Grade: ${grade}`, 20, y);

    doc.save(`Rubric_Report_${studentInfo.studentCode.trim()}_` +
    `${studentInfo.session.trim()}_` +
    `${studentInfo.grade.trim()}_` +
    `${studentInfo.section.trim()}_` +
    `${subject.trim()}_` +
    `${studentInfo.institutionCode.trim()}_` +
    `${studentInfo.teacherCode.trim()}.pdf`);
  };

  return (
    <div className="p-6 mx-auto bg-gray-800 shadow-lg rounded-lg text-white">
      <h2 className="text-2xl font-bold mb-4">AI-Powered Rubric Calculator</h2>
      <div className="mb-4 grid grid-cols-2 gap-4">
        {["studentCode", "session", "grade", "section", "teacherCode", "institutionCode"].map((field) => (
          <input
            key={field}
            name={field}
            placeholder={field.replace(/([A-Z])/g, " $1")}
            value={studentInfo[field]}
            onChange={handleStudentInfoChange}
            className="p-2 border rounded bg-gray-700 text-white w-full"
          />
        ))}
      </div>
      <div className="mb-4">
        <label className="font-semibold">Select Subject:</label>
        <select
          value={selectedSubject}
          onChange={handleSubjectChange}
          className="ml-2 p-2 border rounded bg-gray-700 text-white h-10"
        >
          {Object.keys(rubrics).map((subj, index) => (
            <option key={index} value={subj}>
              {subj}
            </option>
          ))}
        </select>
      </div>
      <textarea 
        className="w-full p-2 mb-4 bg-gray-600 text-white" 
        placeholder="Enter your question here..." 
        onChange={handleQuestionChange}
        style={{width: "100%", height: "250px"}}>
      </textarea>
      <textarea
        placeholder="Enter Student's Answer"
        onChange={handleStudentAnswerChange}
        className="w-full p-2 border rounded mt-2 bg-gray-600 text-white"
        style={{ width: "100%", height: "250px" }}
      ></textarea>
      <div className="flex flex-row justify-center mb-4"> 
        <button disabled={true} className="bg-blue-500 p-2 m-1">Upload</button>
        <button disabled={true} className="bg-green-500 p-2 m-1">Extract Text</button>
      </div>

      {/* Rubric Input Fields */}
      <div className="px-2 py-2 mb-4 border border-red-400">
        {criteria.map((criterion, index) => (
          <div key={index} className="mb-6">
            <label className="font-semibold block py-2 px-2">{criterion.name} ({criterion.weight}%): </label>
            <input
              type="number"
              min="0"
              max="10"
              value={criterion.score}
              onChange={(e) => handleScoreChange(index, parseFloat(e.target.value))}
              className="w-full p-2 border rounded mt-1 bg-gray-200 text-black"
              style={{ width: "150px" }}
            />
          </div>
        ))}

        {/* Show AI Prompt */}
        {studentInfo.aiPrompt && (
          <div className="px-2 py-2 border-2 border-blue-500 mb-4">
            <p className="mt-2 text-sm text-yellow-300">
              AI Prompt: {`Evaluate the following response based on '${criterion.name}' - "${studentInfo.aiPrompt}". Provide constructive feedback.`}
            </p>
          </div>
        )}
      </div>
      <div className="flex flex-row justify-center mb-4"> 
        <button
          onClick={generateAIPrompt}
          // disabled={true}
          className="bg-orange-500 p-2 m-1"
        >
          Ask ChatGPT AI
        </button>
        {/* {studentInfo.question}{' iii '}{studentInfo.studentAnswer} */}
        <button className="bg-orange-500 p-2 m-1" onClick={getFeedbackFromDeepSeek} disabled={isLoading}>
          {isLoading ? 'Processing...' : 'Get DeepSeek Feedback'}
        </button>
      </div>
      {feedback && (
        <div>
          <h2>Feedback:</h2>
          <p>{feedback}</p>
        </div>
      )}
      {/* { aiClicked && (
        <>

        </>
      )} */}

      {/* AI Feedback */}
      <div>
        <textarea
          placeholder="AI Feedback"
          value={studentInfo.aiFeedback}
          className="w-full p-2 border rounded mt-2 bg-gray-300 text-black"
          style={{ width: "100%", height: "250px" }}
        ></textarea>
      </div>

      <div className="mt-4 text-lg font-bold">Total Score: {calculateTotal()}%</div>
      <div className="mt-2 text-xl font-bold text-yellow-300">Grade: {getGrade(calculateTotal())}</div>

      <div className="mt-6">
        <h3 className="text-lg font-bold">Rubrics Analysis</h3>
        {savedRubrics.map((rubric, index) => (
          <div key={index} className="border p-2 mt-2 bg-gray-700">
            <p>Total Score: {rubric.totalScore}%</p>
            <p className="text-yellow-300">Grade: {rubric.grade}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-row justify-center">  
        <button
          onClick={saveRubric}
          className="mr-8 mt-4 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Save Rubric to Think5Whys DeepTeach Cloud Database
        </button>
        <button onClick={generatePDF} className="bg-red-500 p-2 mt-4">Download Rubric Feedback Report</button>
      </div>
    </div>
  );
};

export default RubricCalculator;

